import { useEffect, useState } from "react";
import { Star, Mail, Globe, Package } from "lucide-react";
import StoreLayout from "@/layouts/storeLayout";
import { useParams } from "react-router-dom";
import useStore from "@/hooks/use-store";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import { cn } from "@/lib/utils";
import { LANG } from "@/types";
import useItems from "@/hooks/use-item";
import ProductCard from "@/pages/(MarketPlace)/home/components/product-card";

interface Store {
  id: string;
  name: string;
  bioEn: string;
  bioAr: string;
  email: string;
  website: string;
  imgUrl: string;
  coverUrl: string;
  rating: number;
  reviewCount: number;
  products: Product[];
}

interface Product {
  id: string;
  name: string;
  price: number;
  images: string[];
  rating: number;
  reviewCount: number;
  categories: string[];
  companyId: string;
  store: {
    name: string;
    city?: string;
    region?: string;
  };
  minOrder: number;
  unit: string;
  isFavorite?: boolean;
  quantity?: number;
}

function StoreView() {
  const { id } = useParams();
  const [store, setStore] = useState<Store | null>(null);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { getStoreById } = useStore();
  const { allMarketplaceProduct } = useItems();

  useEffect(() => {
    const fetchStore = async () => {
      if (id) {
        const store = await getStoreById(id);
        setIsLoading(false);
        setStore(store);
      }
    };
    const fetchProducts = async () => {
      if (id) {
        const products = await allMarketplaceProduct({ storeId: id });
        setProducts(products);
      }
    };

    if (id) {
      fetchProducts();
      fetchStore();
    }
  }, [id]);

  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  if (isLoading) {
    return (
      <StoreLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#18181b]"></div>
            <p className="text-gray-600">{t("store.view.loadingStore")}</p>
          </div>
        </div>
      </StoreLayout>
    );
  }

  if (!store) {
    return (
      <StoreLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-xl font-semibold mb-2">
              {t("store.view.storeNotFound")}
            </div>
            <p className="text-gray-600">
              {t("store.view.storeNotFoundDescription")}
            </p>
          </div>
        </div>
      </StoreLayout>
    );
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={cn(
          "w-4 h-4",
          index < rating ? "text-yellow-400 fill-current" : "text-gray-300"
        )}
      />
    ));
  };

  return (
    <StoreLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative h-[400px] w-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 z-10"></div>
          <img
            src={store.coverUrl}
            alt={`${store.name} cover`}
            className="w-full h-full object-cover"
          />
          <div
            className={cn(
              "absolute bottom-8 z-20",
              locale == LANG.AR ? "right-8" : "left-8"
            )}
          >
            <div className="flex items-end space-x-4">
              <div className="relative">
                <div className="w-32 h-32 border-4 border-white rounded-full overflow-hidden shadow-lg">
                  <img
                    src={store.imgUrl}
                    alt={store.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-2 -right-2 bg-green-500 w-8 h-8 rounded-full border-2 border-white flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="mb-4">
                <h1 className="text-4xl font-bold text-white mb-2">
                  {store.name}
                </h1>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center">
                    {renderStars(store.rating)}
                  </div>
                  <span className="text-white/80 text-sm">
                    {store.rating} ({store.reviewCount} reviews)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Store Description */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t("store.view.aboutStore")} {store.name}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {locale == LANG.AR ? store.bioAr : store.bioEn}
                </p>
              </div>

              {/* Products Section */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {t("supplier_sidebar.products")}
                  </h2>
                  <span className="text-sm text-gray-500">
                    {products?.length || 0} {t("store.view.productsAvailable")}
                  </span>
                </div>

                {products && products.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        viewMode="grid"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t("store.view.noProductsAvailable")}
                    </h3>
                    <p className="text-gray-600">
                      {t("store.view.noProductsDescription")}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("store.view.contactInformation")}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#18181b]/10 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#18181b]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {t("store.view.email")}
                      </p>
                      <p className="text-gray-900 font-medium">{store.email}</p>
                    </div>
                  </div>

                  {store.website && (
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#18181b]/10 rounded-full flex items-center justify-center">
                        <Globe className="w-5 h-5 text-[#18181b]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          {t("store.view.website")}
                        </p>
                        <a
                          href={store.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#18181b] hover:underline font-medium"
                        >
                          {store.website}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Store Stats */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("store.view.storeStatistics")}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">
                      {t("store.view.rating")}
                    </span>
                    <div className="flex items-center space-x-1">
                      {renderStars(store.rating)}
                      <span className="text-sm font-medium text-gray-900 ml-1">
                        {store.rating}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">
                      {t("store.view.reviews")}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {store.reviewCount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">
                      {t("store.view.products")}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {products?.length || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button className="w-full bg-[#18181b] text-white py-3 px-4 rounded-xl font-semibold hover:bg-[#18181b]/90 transition-colors">
                  {t("store.view.contactStore")}
                </button>
                <button className="w-full bg-white text-[#18181b] border-2 border-[#18181b] py-3 px-4 rounded-xl font-semibold hover:bg-[#18181b] hover:text-white transition-colors">
                  {t("store.view.followStore")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StoreLayout>
  );
}

export default StoreView;
